import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams as useUrlSearchParams } from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Tooltip,
    IconButton,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    CardActions,
    CircularProgress,
    Divider,
} from "@mui/material";
import {
    Add,
    Person,
    InfoOutlined,
    Receipt,
    FilterList,
    Assessment,
    FileDownload,
    LocalShipping,
    Visibility,
    CallSplit as ChannelIcon,
    UploadFile,
    Delete as DeleteIcon,
} from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid";
import { ServerSideDataTable } from "../../components/common/DataTable";
import { QuickToolLinks } from "../../components/common/QuickToolLinks";
import { OrderFormPrefill } from "../../components/forms/OrderForm";
import { OrderImportDialog } from "../../components/orders/OrderImportDialog";
import { OrderSearchBar } from "../../components/orders/OrderSearchBar";
import { OrderAdvancedSearch } from "../../components/orders/OrderAdvancedSearch";
import { orderService } from "../../services/orderService";
import { customerService } from "../../services/customerService";
import { getCustomerFullName } from "../../utils/customerUtils";
import { formatCurrencyBDT } from "../../utils/currency";
import { formatDateShort } from "../../utils/date";
import { Order, OrderSearchParams } from "../../types";
import { useColumnVisibility } from "../../hooks/useColumnVisibility";
import { OrderDetailDrawer } from "../../components/orders/OrderDetailDrawer";
import { useOrderActions } from "../../components/orders/useOrderActions";
import { useCustomerDetails } from "../../components/customers/useCustomerDetails";
import { CustomerNameLink } from "../../components/customers/CustomerNameLink";
import { usePermissions } from "../../hooks/usePermissions";
import {
    hasActiveOrderSearchParams,
    serializeOrderSearchParams,
} from "../../utils/orderSearchParams";

const getStatusColor = (status: Order["status"]) => {
    switch (status) {
        case "DRAFT":
            return "default";
        case "PENDING":
            return "warning";
        case "CONFIRMED":
            return "info";
        case "SHIPPED":
            return "primary";
        case "DELIVERED":
            return "success";
        case "CANCELLED":
            return "error";
        default:
            return "default";
    }
};

const getPaymentColor = (status: string) => {
    switch (status) {
        case "PAID":
            return "success";
        case "PARTIAL":
            return "warning";
        case "OVERPAID":
            return "info";
        case "REFUNDED":
            return "default";
        default:
            return "error";
    }
};

// The list's starting search: newest orders first, no filters.
const DEFAULT_ORDER_SEARCH: OrderSearchParams = {
    skip: 0,
    limit: 50,
    sort_by: "order_date",
    sort_order: "desc",
};

export const OrderList: React.FC = () => {
    const { canWrite } = usePermissions();
    // `/orders?customer_id=…` (the customer dialog's "All {name}'s Orders"
    // from another page) opens this list already filtered to that customer.
    const [urlParams, setUrlParams] = useUrlSearchParams();
    const urlCustomerId = urlParams.get("customer_id");
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
    const [detailOrder, setDetailOrder] = useState<Order | null>(null);
    const [detailOrderIndex, setDetailOrderIndex] = useState<number>(-1);

    // Pagination and filtering state
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 50,
    });
    const [totalRows, setTotalRows] = useState(0);
    const [statusFilter, setStatusFilter] = useState<Order["status"] | "ALL">(
        "ALL",
    );
    const [daysFilter, setDaysFilter] = useState<number | "">("");
    const [showFilters, setShowFilters] = useState(false);

    // Column visibility state
    const { columnVisibility } = useColumnVisibility("orders");
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Search state
    const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
    const [searchParams, setSearchParams] = useState<OrderSearchParams>(() =>
        urlCustomerId
            ? { ...DEFAULT_ORDER_SEARCH, customer_id: urlCustomerId }
            : DEFAULT_ORDER_SEARCH,
    );
    // Name shown on the customer filter chip.
    const [customerFilterName, setCustomerFilterName] = useState<string | null>(null);

    // Export state
    const [exporting, setExporting] = useState(false);

    // Clicking a customer's name opens the same details dialog as the
    // Customers page. Editing the customer there can change the name shown
    // in these rows, so reload them (pets/addresses don't appear here).
    const customerDetails = useCustomerDetails({
        onCustomerUpdated: (_customer, change) => {
            if (change === "profile") void handleRefresh();
        },
        // Already on this page: filter in place (the dialog has closed).
        onShowOrders: (customer) =>
            showCustomerOrders(customer.customer_id, getCustomerFullName(customer)),
    });

    // Receipt, add/import, edit, clone, status, payment and courier — the
    // same actions and dialogs the Delivery Planner's drawer uses.
    const orderActions = useOrderActions({
        onOrderUpdated: (fresh) => {
            // Keep the open drawer in step (new status, payments, consignment).
            setDetailOrder((current) =>
                current?.order_id === fresh.order_id ? fresh : current,
            );
            void handleRefresh();
        },
        onOrderCreated: () => void handleRefresh(),
        onError: setError,
    });

    useEffect(() => {
        fetchOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel, statusFilter, daysFilter, searchParams]);

    const handleSearch = (params: OrderSearchParams) => {
        setSearchParams(params);
        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
        if (!params.customer_id) setCustomerFilterName(null);
    };

    // All of one customer's orders: their filter replaces every other one
    // (search text, status, date) so nothing is hidden.
    const showCustomerOrders = (customerId: string, name?: string) => {
        setDetailDrawerOpen(false);
        setStatusFilter("ALL");
        setDaysFilter("");
        handleSearch({ ...DEFAULT_ORDER_SEARCH, customer_id: customerId });
        setCustomerFilterName(name || null);
    };

    const clearCustomerFilter = () =>
        handleSearch({ ...searchParams, customer_id: undefined, skip: 0 });

    // Arrived with ?customer_id=… : make sure that filter is applied (the
    // initial state already covers a fresh load), fetch the name for the
    // chip, then drop the parameter — from here the filter lives in the
    // page like any other, so later changes can't fight a stale URL.
    useEffect(() => {
        if (!urlCustomerId) return;
        if (searchParams.customer_id !== urlCustomerId) showCustomerOrders(urlCustomerId);
        customerService
            .getCustomerById(urlCustomerId)
            .then((customer) => setCustomerFilterName(getCustomerFullName(customer)))
            .catch(() => undefined);
        setUrlParams({}, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlCustomerId]);

    const handleExport = async () => {
        setExporting(true);
        try {
            // Build the current effective search params including active filters
            const effectiveParams: any = { ...searchParams };

            // Add status filter if active
            if (statusFilter && statusFilter !== "ALL") {
                effectiveParams.status = statusFilter;
            }

            // Add days filter if active
            if (daysFilter !== "") {
                const today = new Date();
                const fromDate = new Date(today);
                fromDate.setDate(today.getDate() - Number(daysFilter));
                effectiveParams.order_date_from = fromDate
                    .toISOString()
                    .split("T")[0];
                effectiveParams.order_date_to = today
                    .toISOString()
                    .split("T")[0];
            }

            const blob = await orderService.exportOrders(effectiveParams);

            // Create download link
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement("a");
            link.href = url;
            const dateStr = new Date().toISOString().split("T")[0];
            link.setAttribute("download", `orders_export_${dateStr}.csv`);
            document.body.appendChild(link);
            link.click();
            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            console.error("Failed to export orders:", error);
            setError("Failed to export orders");
        } finally {
            setExporting(false);
        }
    };

    const handleOpenStats = () => {
        // Build the current effective search params including active filters
        const effectiveParams: any = { ...searchParams };

        // Add status filter if active
        if (statusFilter && statusFilter !== "ALL") {
            effectiveParams.status = statusFilter;
        }

        // Add days filter if active
        if (daysFilter !== "") {
            const today = new Date();
            const fromDate = new Date(today);
            fromDate.setDate(today.getDate() - Number(daysFilter));
            effectiveParams.order_date_from = fromDate
                .toISOString()
                .split("T")[0];
            effectiveParams.order_date_to = today.toISOString().split("T")[0];
        }

        // Build URL with search params
        const params = serializeOrderSearchParams(effectiveParams);

        // Open statistics page in new tab
        const url = `/orders/statistics?${params.toString()}`;
        window.open(url, "_blank");
    };

    const fetchOrders = async (): Promise<Order[]> => {
        try {
            setLoading(true);

            // Check if status or days filters are active
            const hasStatusFilter = statusFilter && statusFilter !== "ALL";
            const hasDaysFilter = daysFilter !== "";

            // Use search if any search params are set OR if status/days filters are active
            const hasSearchParams =
                hasActiveOrderSearchParams(searchParams) ||
                hasStatusFilter ||
                hasDaysFilter;

            let items: Order[] = [];

            if (hasSearchParams) {
                // Use new search endpoint
                const searchParamsWithFilters: any = {
                    ...searchParams,
                    skip: paginationModel.page * paginationModel.pageSize,
                    limit: paginationModel.pageSize,
                };

                // Apply status filter to search params
                if (hasStatusFilter) {
                    searchParamsWithFilters.status = statusFilter;
                }

                // Apply days filter to search params (convert to date range)
                if (hasDaysFilter) {
                    const today = new Date();
                    const fromDate = new Date(today);
                    fromDate.setDate(today.getDate() - Number(daysFilter));
                    searchParamsWithFilters.order_date_from = fromDate
                        .toISOString()
                        .split("T")[0];
                    searchParamsWithFilters.order_date_to = today
                        .toISOString()
                        .split("T")[0];
                }

                const response = await orderService.searchOrders(
                    searchParamsWithFilters,
                );
                items = response.items;
                setOrders(items);
                setTotalRows(response.total);
            } else {
                // Use old getOrders endpoint for backward compatibility (no filters at all)
                const params: any = {
                    skip: paginationModel.page * paginationModel.pageSize,
                    limit: paginationModel.pageSize,
                };

                const response = await orderService.getOrders(params);
                items = response.items;
                setOrders(items);
                setTotalRows(response.total);
            }
            return items;
        } catch (err: any) {
            setError("Failed to load orders");
            console.error("Orders fetch error:", err);
            return [];
        } finally {
            setLoading(false);
        }
    };

    /**
     * After any list refresh, re-sync the drawer's position within the new list.
     * - Order still present  → update index to its new position.
     * - Order filtered out   → navigate drawer to the order now occupying the
     *                          same slot (clamped to last), or close if list empty.
     */
    const syncDrawerIndex = useCallback(
        (freshItems: Order[], currentOrderId: string | undefined) => {
            if (!detailDrawerOpen || !currentOrderId) return;

            const newIdx = freshItems.findIndex(
                (o) => o.order_id === currentOrderId,
            );

            if (newIdx !== -1) {
                // Order still in list — just sync the index
                setDetailOrderIndex(newIdx);
                return;
            }

            // Order was filtered out of the new list
            if (freshItems.length === 0) {
                // Nothing left — close the drawer
                setDetailDrawerOpen(false);
                setDetailOrderIndex(-1);
                return;
            }

            // Navigate to the order that now occupies the same slot
            const targetIdx = Math.min(detailOrderIndex, freshItems.length - 1);
            const targetOrder = freshItems[targetIdx];
            setDetailOrderIndex(targetIdx);
            setDetailOrder(targetOrder as Order);

            // Silently upgrade to full order detail in the background
            orderService
                .getOrderById(targetOrder.order_id)
                .then(setDetailOrder)
                .catch(() => {});
        },
        [detailDrawerOpen, detailOrderIndex],
    );

    const handleOpenImport = () => {
        setImportDialogOpen(true);
    };

    const handleImportResolved = (prefill: OrderFormPrefill) => {
        setImportDialogOpen(false);
        orderActions.createOrder(prefill);
    };

    const handleViewDetail = async (id: string) => {
        try {
            setLoading(true);
            const idx = orders.findIndex((o) => o.order_id === id);
            const detailedOrder = await orderService.getOrderById(id);
            setDetailOrder(detailedOrder);
            setDetailOrderIndex(idx);
            setDetailDrawerOpen(true);
        } catch (err: any) {
            setError("Failed to load order details");
            console.error("Error fetching order details:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleNavigateDetail = useCallback(
        async (direction: "prev" | "next") => {
            const nextIndex =
                direction === "prev"
                    ? detailOrderIndex - 1
                    : detailOrderIndex + 1;
            if (nextIndex < 0 || nextIndex >= orders.length) return;
            const targetOrder = orders[nextIndex];
            setDetailOrderIndex(nextIndex);
            setDetailOrder(targetOrder as Order);
            try {
                const full = await orderService.getOrderById(
                    targetOrder.order_id,
                );
                setDetailOrder(full);
            } catch {
                // already showing list-level data; silently ignore
            }
        },
        [detailOrderIndex, orders],
    );

    const handleRefresh = async () => {
        const freshItems = await fetchOrders();
        syncDrawerIndex(freshItems, detailOrder?.order_id);
    };

    const handleDiscardDraft = async (orderId: string, orderNumber: string) => {
        if (
            !window.confirm(`Discard draft order #${orderNumber}? This can't be undone.`)
        ) {
            return;
        }
        try {
            await orderService.deleteOrder(orderId);
            await handleRefresh();
        } catch (err: any) {
            setError(
                err.response?.data?.detail || "Failed to discard draft order",
            );
        }
    };

    const handlePaginationModelChange = (model: {
        page: number;
        pageSize: number;
    }) => {
        setPaginationModel(model);
    };

    const handleStatusFilterChange = (event: any) => {
        setStatusFilter(event.target.value);
        setPaginationModel({ ...paginationModel, page: 0 }); // Reset to first page
    };

    const handleDaysFilterChange = (event: any) => {
        const value = event.target.value;
        setDaysFilter(value === "" ? "" : Number(value));
        setPaginationModel({ ...paginationModel, page: 0 }); // Reset to first page
    };

    const handleClearFilters = () => {
        setStatusFilter("ALL");
        setDaysFilter("");
        setPaginationModel({ page: 0, pageSize: 50 });
    };

    const columns: GridColDef[] = [
        {
            field: "customer",
            headerName: "Customer",
            flex: 0.8,
            minWidth: 180,
            maxWidth: 260,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Person fontSize="small" color="action" />
                    <Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <CustomerNameLink
                                customerId={params.row.customer_id}
                                name={params.row.customer_name}
                                onOpen={customerDetails.showCustomer}
                                loading={
                                    customerDetails.loadingCustomerId ===
                                    params.row.customer_id
                                }
                            />
                            {params.row.notes && (
                                <Tooltip title={params.row.notes} arrow>
                                    <InfoOutlined
                                        fontSize="small"
                                        sx={{
                                            cursor: "pointer",
                                            color: "info.main",
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                // fontWeight="italic"
                            >
                                {params.row.order_number}
                            </Typography>
                            {params.row.status && (
                                <Chip
                                    label={params.row.status}
                                    size="small"
                                    color={
                                        getStatusColor(params.row.status) as any
                                    }
                                    sx={{
                                        height: 18,
                                        fontSize: "0.65rem",
                                    }}
                                />
                            )}
                        </Box>
                        {params.row.customer_email && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                            >
                                {params.row.customer_email}
                            </Typography>
                        )}
                    </Box>
                </Box>
            ),
        },
        {
            field: "total_amount",
            headerName: "Total",
            width: 150,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="primary"
                    >
                        {formatCurrencyBDT(Number(params.value) || 0)}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "detail_actions",
            headerName: "Detail",
            width: 110,
            sortable: false,
            renderCell: (params) => (
                <Button
                    size="small"
                    startIcon={<Visibility />}
                    onClick={() => handleViewDetail(params.row.order_id)}
                    variant="contained"
                    color="primary"
                >
                    View
                </Button>
            ),
        },
        {
            field: "receipt_actions",
            headerName: "Receipt",
            width: 120,
            sortable: false,
            renderCell: (params) => (
                <Button
                    size="small"
                    startIcon={<Receipt />}
                    onClick={() => orderActions.openReceipt(params.row.order_id)}
                    variant="outlined"
                    color="secondary"
                >
                    Print
                </Button>
            ),
        },
        {
            field: "payment_status",
            headerName: "Payment",
            width: 130,
            renderCell: (params) => {
                if (params.row.status === "CANCELLED") {
                    return (
                        <Typography variant="body2" color="text.disabled">
                            -
                        </Typography>
                    );
                }

                const paymentStatus = params.row.payment_status || "UNPAID";
                return (
                    <Chip
                        label={paymentStatus}
                        color={getPaymentColor(paymentStatus)}
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: "medium" }}
                    />
                );
            },
        },
        {
            field: "order_date",
            headerName: "Order Date",
            width: 150,
            renderCell: (params) => (
                <Typography variant="body2">
                    {formatDateShort(params.value)}
                </Typography>
            ),
        },
        {
            field: "items_total_calculated",
            headerName: "Items Total",
            width: 150,
            renderCell: (params) => {
                const finalTotal = Number(params.row.total_amount) || 0;
                const shippingAmount = Number(params.row.shipping_amount) || 0;
                const discountAmount = Number(params.row.discount_amount) || 0;
                const itemsTotal = finalTotal - shippingAmount + discountAmount;

                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                            {formatCurrencyBDT(itemsTotal)}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: "delivery_service_name",
            headerName: "Delivery Service",
            width: 150,
            renderCell: (params) => {
                const deliveryServiceName = params.row.delivery_service_name;
                const deliveryServiceType = params.row.delivery_service_type;

                if (!deliveryServiceName) {
                    return (
                        <Typography variant="body2" color="text.disabled">
                            None
                        </Typography>
                    );
                }

                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <LocalShipping fontSize="small" color="action" />
                        <Box>
                            <Typography variant="body2">
                                {deliveryServiceName}
                            </Typography>
                            {deliveryServiceType && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {deliveryServiceType}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            },
        },
        {
            field: "order_channel_name",
            headerName: "Channel",
            width: 150,
            renderCell: (params) => {
                const orderChannelName = params.row.order_channel_name;
                const orderChannelType = params.row.order_channel_type;

                if (!orderChannelName) {
                    return (
                        <Typography variant="body2" color="text.disabled">
                            None
                        </Typography>
                    );
                }

                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <ChannelIcon fontSize="small" color="action" />
                        <Box>
                            <Typography variant="body2">
                                {orderChannelName}
                            </Typography>
                            {orderChannelType && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {orderChannelType}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            },
        },
        {
            field: "total_cost_price",
            headerName: "Cost Price",
            width: 130,
            renderCell: (params) => {
                const totalCostPrice = Number(params.row.total_cost_price) || 0;
                return (
                    <Typography variant="body2" color="text.secondary">
                        {formatCurrencyBDT(totalCostPrice)}
                    </Typography>
                );
            },
        },
        {
            field: "profit",
            headerName: "Profit",
            width: 130,
            renderCell: (params) => {
                const totalAmount = Number(params.row.total_amount) || 0;
                const totalCostPrice = Number(params.row.total_cost_price) || 0;
                const deliveryCost = Number(params.row.delivery_cost) || 0;
                const netProfit = totalAmount - totalCostPrice - deliveryCost;

                if (netProfit <= 0) {
                    return (
                        <Typography variant="body2" color="error.main">
                            {formatCurrencyBDT(netProfit)}
                        </Typography>
                    );
                }

                return (
                    <Typography variant="body2" color="success.main">
                        {formatCurrencyBDT(netProfit)}
                    </Typography>
                );
            },
        },
        {
            field: "shipping_amount",
            headerName: "Delivery",
            width: 160,
            renderCell: (params) => {
                const shippingAmount = Number(params.value) || 0;
                const deliveryCost = Number(params.row.delivery_cost) || 0;

                return (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                        }}
                    >
                        {shippingAmount > 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                Charge: {formatCurrencyBDT(shippingAmount)}
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="text.disabled">
                                Charge: -
                            </Typography>
                        )}
                        {deliveryCost > 0 ? (
                            <Typography
                                variant="caption"
                                color="warning.main"
                                sx={{ fontSize: "0.75rem" }}
                            >
                                Cost: {formatCurrencyBDT(deliveryCost)}
                            </Typography>
                        ) : (
                            <Typography
                                variant="caption"
                                color="text.disabled"
                                sx={{ fontSize: "0.75rem" }}
                            >
                                Cost: -
                            </Typography>
                        )}
                    </Box>
                );
            },
        },
        {
            field: "discount_amount",
            headerName: "Discount",
            width: 130,
            renderCell: (params) => {
                const discountAmount = Number(params.value) || 0;
                return discountAmount > 0 ? (
                    <Typography variant="body2" color="success.main">
                        -{formatCurrencyBDT(discountAmount)}
                    </Typography>
                ) : (
                    <Typography variant="body2" color="text.disabled">
                        -
                    </Typography>
                );
            },
        },
    ];

    // Filter columns based on visibility settings
    const visibleColumns = columns.filter((column) => {
        // Map column fields to visibility keys
        const visibilityMap: { [key: string]: string } = {
            customer: "customer_name",
            order_number: "order_number",
            status: "status",
            total_amount: "total_amount",
            total_cost_price: "total_cost_price",
            profit: "profit",
            shipping_amount: "shipping_amount",
            delivery_service: "delivery_service",
            order_channel_name: "order_channel",
            order_date: "order_date",
            payment_status: "payment_status",
            actions: "actions",
        };

        const visibilityKey = visibilityMap[column.field];
        return visibilityKey ? columnVisibility[visibilityKey] !== false : true;
    });

    const rows = (orders || []).map((order) => ({
        id: order.order_id,
        ...order,
    }));

    return (
        <Box sx={{ position: "relative" }}>
            {/* Fixed Header with Buttons and Filters */}
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "background.paper",
                    pb: 2,
                    mb: 3,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    width: "100%",
                    maxWidth: "100vw",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                        flexWrap: "wrap",
                        gap: 1,
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{
                            flexGrow: 1,
                            fontSize: { xs: "1.35rem", sm: "2.125rem" },
                        }}
                    >
                        Orders ({totalRows})
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <QuickToolLinks
                            toolIds={[
                                "delivery-planner",
                                "daily-cash-flow-analyzer",
                            ]}
                        />
                        <Tooltip
                            title={exporting ? "Exporting..." : "Export CSV"}
                        >
                            <Button
                                variant="outlined"
                                startIcon={<FileDownload />}
                                onClick={handleExport}
                                disabled={exporting}
                                color="success"
                                size="small"
                            >
                                {isMobile
                                    ? exporting
                                        ? "..."
                                        : "CSV"
                                    : exporting
                                      ? "Exporting..."
                                      : "Export CSV"}
                            </Button>
                        </Tooltip>
                        <Tooltip title="Order Statistics">
                            <Button
                                variant="outlined"
                                startIcon={<Assessment />}
                                onClick={handleOpenStats}
                                color="info"
                                size="small"
                            >
                                Stats
                            </Button>
                        </Tooltip>
                        <Tooltip
                            title={
                                showFilters ? "Hide Filters" : "Show Filters"
                            }
                        >
                            <Button
                                variant="outlined"
                                startIcon={<FilterList />}
                                onClick={() => setShowFilters(!showFilters)}
                                size="small"
                            >
                                {isMobile
                                    ? showFilters
                                        ? "Hide"
                                        : "Filter"
                                    : showFilters
                                      ? "Hide Filters"
                                      : "Show Filters"}
                            </Button>
                        </Tooltip>
                        {canWrite && (
                            <Tooltip title="Import order from a chat-parsed JSON payload">
                                <Button
                                    variant="outlined"
                                    startIcon={<UploadFile />}
                                    onClick={handleOpenImport}
                                    size="small"
                                >
                                    {isMobile ? "Import" : "Import from JSON"}
                                </Button>
                            </Tooltip>
                        )}
                        {canWrite && (
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => orderActions.createOrder()}
                                size="small"
                            >
                                {isMobile ? "New" : "Create Order"}
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Search Bar */}
                <OrderSearchBar
                    onSearch={handleSearch}
                    onToggleAdvanced={() => setAdvancedSearchOpen(true)}
                    searchParams={searchParams}
                />

                {searchParams.customer_id && (
                    <Box sx={{ mb: 2 }}>
                        <Chip
                            icon={<Person />}
                            color="primary"
                            label={`Customer: ${customerFilterName ?? "…"}`}
                            onDelete={clearCustomerFilter}
                            title="Showing this customer's orders only — remove to see all orders"
                        />
                    </Box>
                )}

                {/* Filter Controls */}
                {showFilters && (
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                            alignItems: "center",
                            p: 2,
                            backgroundColor: "action.hover",
                            borderRadius: 1,
                        }}
                    >
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                label="Status"
                                onChange={handleStatusFilterChange}
                            >
                                <MenuItem value="ALL">All Statuses</MenuItem>
                                <MenuItem value="DRAFT">Draft</MenuItem>
                                <MenuItem value="PENDING">Pending</MenuItem>
                                <MenuItem value="CONFIRMED">Confirmed</MenuItem>
                                <MenuItem value="SHIPPED">Shipped</MenuItem>
                                <MenuItem value="DELIVERED">Delivered</MenuItem>
                                <MenuItem value="CANCELLED">Cancelled</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Time Period</InputLabel>
                            <Select
                                value={daysFilter}
                                label="Time Period"
                                onChange={handleDaysFilterChange}
                            >
                                <MenuItem value="">All Time</MenuItem>
                                <MenuItem value={7}>Last 7 Days</MenuItem>
                                <MenuItem value={30}>Last 30 Days</MenuItem>
                                <MenuItem value={90}>Last 90 Days</MenuItem>
                                <MenuItem value={180}>Last 6 Months</MenuItem>
                                <MenuItem value={365}>Last Year</MenuItem>
                            </Select>
                        </FormControl>

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleClearFilters}
                        >
                            Clear Filters
                        </Button>
                    </Box>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Mobile Card View / Desktop DataGrid */}
            {isMobile ? (
                <Box sx={{ mt: 1 }}>
                    {loading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 6,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : rows.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 6 }}>
                            <Typography color="text.secondary">
                                No orders found.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {(rows as any[]).map((order) => (
                                <Card
                                    key={order.id}
                                    variant="outlined"
                                    sx={{ mb: 1.5, borderRadius: 2 }}
                                >
                                    <CardContent sx={{ pb: "8px !important" }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                mb: 0.5,
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                fontWeight="medium"
                                            >
                                                {order.order_number}
                                            </Typography>
                                            <Chip
                                                label={order.status}
                                                size="small"
                                                color={
                                                    getStatusColor(
                                                        order.status,
                                                    ) as any
                                                }
                                                sx={{
                                                    height: 20,
                                                    fontSize: "0.65rem",
                                                }}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                mb: 0.5,
                                            }}
                                        >
                                            <CustomerNameLink
                                                customerId={order.customer_id}
                                                name={order.customer_name}
                                                onOpen={customerDetails.showCustomer}
                                                loading={
                                                    customerDetails.loadingCustomerId ===
                                                    order.customer_id
                                                }
                                                variant="body1"
                                                fontWeight="medium"
                                                noWrap
                                            />
                                            {order.notes && (
                                                <Tooltip
                                                    title={order.notes}
                                                    arrow
                                                >
                                                    <InfoOutlined
                                                        fontSize="small"
                                                        sx={{
                                                            cursor: "pointer",
                                                            color: "info.main",
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Box>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                color="primary.main"
                                                fontWeight="bold"
                                            >
                                                {formatCurrencyBDT(
                                                    Number(
                                                        order.total_amount,
                                                    ) || 0,
                                                )}
                                            </Typography>
                                            {order.status !== "CANCELLED" && (
                                                <Chip
                                                    label={
                                                        order.payment_status ||
                                                        "UNPAID"
                                                    }
                                                    size="small"
                                                    color={
                                                        getPaymentColor(
                                                            order.payment_status ||
                                                                "UNPAID",
                                                        ) as any
                                                    }
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                mt: 0.75,
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {formatDateShort(
                                                    order.order_date,
                                                )}
                                            </Typography>
                                            {order.delivery_service_name && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {
                                                        order.delivery_service_name
                                                    }
                                                </Typography>
                                            )}
                                            {order.order_channel_name && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {
                                                        order.order_channel_name
                                                    }
                                                </Typography>
                                            )}
                                        </Box>
                                    </CardContent>

                                    <Divider />

                                    <CardActions
                                        sx={{ px: 1.5, py: 1, gap: 1 }}
                                    >
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<Visibility />}
                                            onClick={() =>
                                                handleViewDetail(order.order_id)
                                            }
                                            sx={{ flex: 1 }}
                                        >
                                            View
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="secondary"
                                            startIcon={<Receipt />}
                                            onClick={() =>
                                                orderActions.openReceipt(
                                                    order.order_id,
                                                )
                                            }
                                            sx={{ flex: 1 }}
                                        >
                                            Receipt
                                        </Button>
                                        {order.status === "DRAFT" &&
                                            canWrite && (
                                                <Tooltip title="Discard Draft">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() =>
                                                            handleDiscardDraft(
                                                                order.order_id,
                                                                order.order_number,
                                                            )
                                                        }
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                    </CardActions>
                                </Card>
                            ))}

                            {/* Mobile Pagination */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mt: 1,
                                    py: 1,
                                }}
                            >
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={paginationModel.page === 0}
                                    onClick={() =>
                                        handlePaginationModelChange({
                                            ...paginationModel,
                                            page: paginationModel.page - 1,
                                        })
                                    }
                                >
                                    ← Prev
                                </Button>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {paginationModel.page *
                                        paginationModel.pageSize +
                                        1}
                                    –
                                    {Math.min(
                                        (paginationModel.page + 1) *
                                            paginationModel.pageSize,
                                        totalRows,
                                    )}{" "}
                                    of {totalRows}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={
                                        (paginationModel.page + 1) *
                                            paginationModel.pageSize >=
                                        totalRows
                                    }
                                    onClick={() =>
                                        handlePaginationModelChange({
                                            ...paginationModel,
                                            page: paginationModel.page + 1,
                                        })
                                    }
                                >
                                    Next →
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            ) : (
                <Box
                    sx={{
                        overflowX: "auto",
                        overflowY: "auto",
                        maxHeight: {
                            xs: "calc(100vh - 310px)",
                            sm: "calc(100vh - 250px)",
                        },
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        width: "100%",
                        "&::-webkit-scrollbar": {
                            height: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                            backgroundColor: "action.hover",
                        },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "action.selected",
                            borderRadius: "4px",
                        },
                    }}
                >
                    <Box sx={{ minWidth: "max-content" }}>
                        <ServerSideDataTable
                            fullWidthColumns
                            rows={rows}
                            columns={visibleColumns}
                            loading={loading}
                            hideActions={true}
                            totalRows={totalRows}
                            paginationModel={paginationModel}
                            onPaginationModelChange={
                                handlePaginationModelChange
                            }
                            pageSize={paginationModel.pageSize}
                        />
                    </Box>
                </Box>
            )}

            <OrderImportDialog
                open={importDialogOpen}
                onClose={() => setImportDialogOpen(false)}
                onResolved={handleImportResolved}
            />

            <OrderAdvancedSearch
                open={advancedSearchOpen}
                onClose={() => setAdvancedSearchOpen(false)}
                onSearch={handleSearch}
                initialParams={searchParams}
            />

            <OrderDetailDrawer
                open={detailDrawerOpen}
                onClose={() => setDetailDrawerOpen(false)}
                order={detailOrder}
                onNavigate={handleNavigateDetail}
                hasPrev={detailOrderIndex > 0}
                hasNext={detailOrderIndex < orders.length - 1}
                currentIndex={
                    detailOrderIndex >= 0 ? detailOrderIndex : undefined
                }
                totalCount={orders.length}
                {...orderActions.drawerActionProps}
                onPaymentAdded={(updatedOrder) => {
                    setDetailOrder(updatedOrder);
                    setOrders((prev) =>
                        prev.map((o) =>
                            o.order_id === updatedOrder.order_id
                                ? {
                                      ...o,
                                      payment_status:
                                          updatedOrder.payment_status,
                                      amount_paid: updatedOrder.amount_paid,
                                  }
                                : o,
                        ),
                    );
                }}
                onDiscarded={handleRefresh}
            />

            {orderActions.dialogs}
            {customerDetails.dialog}
        </Box>
    );
};
